import React, {Component} from 'react';
import axios from 'axios';
import * as ReactBootStrap from 'react-bootstrap';
import Chart from 'chart.js';
import '../tools/css/InformeFacturas.css';

const ApiURL="https://medicall-api-test.azurewebsites.net";

// Paleta categórica validada (skill de dataviz) para identificar cada "status" con el mismo color en ambas gráficas.
const PALETA_CATEGORICA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COLOR_MARCA = '#AD1457';
const COLOR_OTROS = '#c3c2b7';
const TEXTO_MUTED = '#898781';
const TEXTO_SECUNDARIO = '#52514e';
const GRID_COLOR = '#e1e0d9';
const EJE_COLOR = '#c3c2b7';

class InformeFacturas extends Component {

  chartEstadoRef = React.createRef();
  chartContratoRef = React.createRef();
  chartEstadoMontoRef = React.createRef();

  chartEstado = null;
  chartContrato = null;
  chartEstadoMonto = null;

  // Mapa estable estado -> color: una vez asignado un color a un estado no cambia,
  // aunque el filtro reduzca los datos (evita "repintar" al filtrar).
  colorPorEstado = {};

  // Tipos de documento vistos hasta ahora (se acumulan entre páginas para que el filtro no "encoja" al paginar).
  tiposDocumentoVistos = new Set();

  constructor() {
    super();
    this.state = {
      cargando: true,
      facturas: [],
      pagerItems: [],
      pagerInfo: '',
      paginaActual: 1,
      filtroEstado: 'todos',
      filtroDocumento: 'todos',
      estadosDisponibles: [],
      tiposDocumentoDisponibles: []
    };
  }

  componentDidMount() {
    this.cargarFacturas(1);
  }

  componentWillUnmount() {
    [this.chartEstado, this.chartContrato, this.chartEstadoMonto].forEach((grafica) => {
      if (grafica) grafica.destroy();
    });
  }

  // La API real de Invoices no está documentada aquí, así que cada campo se
  // resuelve con nombres alternativos por si el backend usa otra convención.
  obtenerEstado = (factura) => factura.status || factura.estado || 'Sin estado';

  obtenerTotal = (factura) => {
    const valor = factura.total ?? factura.value ?? factura.amount ?? factura.valor ?? 0;
    return Number(valor) || 0;
  };

  obtenerContrato = (factura) => {
    if (factura.contract && typeof factura.contract === 'object') return factura.contract.name || 'Sin contrato';
    if (typeof factura.contract === 'string' && factura.contract) return factura.contract;
    if (factura.contrato) return factura.contrato;
    return 'Sin contrato';
  };

  obtenerTipoDocumento = (factura) => factura.documentType || factura.tipoDocumento || factura.document_type || 'Sin tipo';

  registrarTiposDocumento = (facturas) => {
    facturas.forEach((f) => this.tiposDocumentoVistos.add(this.obtenerTipoDocumento(f)));
  };

  asignarColoresEstado = (facturas) => {
    const estadosOrdenados = Array.from(new Set(facturas.map(this.obtenerEstado))).sort((a, b) => a.localeCompare(b));
    estadosOrdenados.forEach((estado) => {
      if (!this.colorPorEstado[estado]) {
        const indice = Object.keys(this.colorPorEstado).length;
        this.colorPorEstado[estado] = indice < PALETA_CATEGORICA.length ? PALETA_CATEGORICA[indice] : COLOR_OTROS;
      }
    });
  };

  cargarFacturas = (pagina) => {
    this.setState({ cargando: true });

    axios.get(ApiURL + "/api/Invoices/List?numeroPagina=" + pagina)
      .then((response) => {
        console.log(response);
        const datos = (response.data && response.data.data) || [];
        this.asignarColoresEstado(datos);
        this.registrarTiposDocumento(datos);

        this.setState({
          facturas: datos,
          pagerItems: response.data.pager ? response.data.pager.items : [],
          pagerInfo: response.data.pager ? response.data.pager.showData : '',
          paginaActual: pagina,
          estadosDisponibles: Object.keys(this.colorPorEstado),
          tiposDocumentoDisponibles: Array.from(this.tiposDocumentoVistos).sort((a, b) => a.localeCompare(b)),
          cargando: false
        }, () => this.dibujarGraficas());
      })
      .catch((error) => {
        console.log(error);
        this.setState({ cargando: false });
      });
  };

  cambiarFiltroEstado = (e) => {
    this.setState({ filtroEstado: e.target.value }, () => this.dibujarGraficas());
  };

  cambiarFiltroDocumento = (e) => {
    this.setState({ filtroDocumento: e.target.value }, () => this.dibujarGraficas());
  };

  facturasFiltradas = () => {
    const { facturas, filtroEstado, filtroDocumento } = this.state;
    return facturas.filter((f) => {
      const coincideEstado = filtroEstado === 'todos' || this.obtenerEstado(f) === filtroEstado;
      const coincideDocumento = filtroDocumento === 'todos' || this.obtenerTipoDocumento(f) === filtroDocumento;
      return coincideEstado && coincideDocumento;
    });
  };

  formatoMoneda = (valor) => '$' + Number(valor || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });

  dibujarGraficas = () => {
    const facturas = this.facturasFiltradas();
    this.dibujarGraficaEstadoCantidad(facturas);
    this.dibujarGraficaContrato(facturas);
    this.dibujarGraficaEstadoMonto(facturas);
  };

  // Doughnut: identidad = color (no hay eje), por eso lleva leyenda y paleta categórica.
  dibujarGraficaEstadoCantidad = (facturas) => {
    const lienzo = this.chartEstadoRef.current;
    if (!lienzo) return;

    const conteo = {};
    facturas.forEach((f) => {
      const estado = this.obtenerEstado(f);
      conteo[estado] = (conteo[estado] || 0) + 1;
    });

    const etiquetas = Object.keys(conteo);
    const datos = etiquetas.map((e) => conteo[e]);
    const colores = etiquetas.map((e) => this.colorPorEstado[e] || COLOR_OTROS);

    if (this.chartEstado) this.chartEstado.destroy();

    this.chartEstado = new Chart(lienzo.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: etiquetas,
        datasets: [{
          data: datos,
          backgroundColor: colores,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 62,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, fontColor: TEXTO_SECUNDARIO, fontSize: 12, padding: 14 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const etiqueta = data.labels[item.index];
              const valor = data.datasets[0].data[item.index];
              return `${etiqueta}: ${valor} atencion${valor === 1 ? '' : 'es'}`;
            }
          }
        }
      }
    });
  };

  // Bar vertical: magnitud comparada entre contratos ya identificados por el eje,
  // por eso usa un solo color (marca) en vez de una paleta categórica ("una barra, un color").
  dibujarGraficaContrato = (facturas) => {
    const lienzo = this.chartContratoRef.current;
    if (!lienzo) return;

    const totalesPorContrato = {};
    facturas.forEach((f) => {
      const contrato = this.obtenerContrato(f);
      totalesPorContrato[contrato] = (totalesPorContrato[contrato] || 0) + this.obtenerTotal(f);
    });

    const ordenados = Object.entries(totalesPorContrato).sort((a, b) => b[1] - a[1]);
    const TOP = 10;
    const principales = ordenados.slice(0, TOP);
    const resto = ordenados.slice(TOP);

    if (resto.length) {
      const sumaResto = resto.reduce((acc, [, valor]) => acc + valor, 0);
      principales.push([`Otros (${resto.length})`, sumaResto]);
    }

    const etiquetas = principales.map(([nombre]) => nombre);
    const datos = principales.map(([, valor]) => valor);

    if (this.chartContrato) this.chartContrato.destroy();

    this.chartContrato = new Chart(lienzo.getContext('2d'), {
      type: 'bar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Total facturado',
          data: datos,
          backgroundColor: COLOR_MARCA,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11, maxRotation: 40, minRotation: 0 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: {
              beginAtZero: true,
              fontColor: TEXTO_MUTED,
              callback: (valor) => this.formatoMoneda(valor)
            }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => this.formatoMoneda(item.yLabel)
          }
        }
      }
    });
  };

  // Bar horizontal: misma identidad de "estado" que el doughnut, mismos colores estables.
  dibujarGraficaEstadoMonto = (facturas) => {
    const lienzo = this.chartEstadoMontoRef.current;
    if (!lienzo) return;

    const totalesPorEstado = {};
    facturas.forEach((f) => {
      const estado = this.obtenerEstado(f);
      totalesPorEstado[estado] = (totalesPorEstado[estado] || 0) + this.obtenerTotal(f);
    });

    const etiquetas = Object.keys(totalesPorEstado);
    const datos = etiquetas.map((e) => totalesPorEstado[e]);
    const colores = etiquetas.map((e) => this.colorPorEstado[e] || COLOR_OTROS);

    if (this.chartEstadoMonto) this.chartEstadoMonto.destroy();

    this.chartEstadoMonto = new Chart(lienzo.getContext('2d'), {
      type: 'horizontalBar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Total facturado',
          data: datos,
          backgroundColor: colores,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, callback: (valor) => this.formatoMoneda(valor) }
          }],
          yAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_SECUNDARIO, fontSize: 12 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => this.formatoMoneda(item.xLabel)
          }
        }
      }
    });
  };

  render() {
    const facturas = this.facturasFiltradas();
    const totalFacturado = facturas.reduce((acc, f) => acc + this.obtenerTotal(f), 0);
    const cantidadFacturas = facturas.length;
    const promedio = cantidadFacturas ? totalFacturado / cantidadFacturas : 0;

    const resumenPorEstado = {};
    facturas.forEach((f) => {
      const estado = this.obtenerEstado(f);
      if (!resumenPorEstado[estado]) resumenPorEstado[estado] = { cantidad: 0, total: 0 };
      resumenPorEstado[estado].cantidad += 1;
      resumenPorEstado[estado].total += this.obtenerTotal(f);
    });

    const resumenPorContrato = {};
    facturas.forEach((f) => {
      const contrato = this.obtenerContrato(f);
      if (!resumenPorContrato[contrato]) resumenPorContrato[contrato] = { cantidad: 0, total: 0 };
      resumenPorContrato[contrato].cantidad += 1;
      resumenPorContrato[contrato].total += this.obtenerTotal(f);
    });

    return (
      <div className="content-wrapper">
        <div className="col-12 col-sm-12">

          <div className="row">
            <div className="col-sm-12">
              <br/>
              <div className="text-center" id="title"><h6><b>INFORME DE ATENCIONES</b></h6></div>
            </div>
          </div>

          <div className="row m24if-kpis">
            <div className="col-md-4">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Total cargos</span>
                <span className="m24if-stat-value">{this.formatoMoneda(totalFacturado)}</span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Cantidad de atenciones</span>
                <span className="m24if-stat-value">{cantidadFacturas.toLocaleString('es-CO')}</span>
              </div>
            </div>
            <div className="col-md-4">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Promedio por atencion</span>
                <span className="m24if-stat-value">{this.formatoMoneda(promedio)}</span>
              </div>
            </div>
          </div>

          <div className="row m24if-filtros">
            <div className="col-sm-4">
              <div className="input-group input-group-sm mb-3">
                <div className="input-group-prepend">
                  <div className="input-group-text">Estado</div>
                </div>
                <select className="form-control" value={this.state.filtroEstado} onChange={this.cambiarFiltroEstado}>
                  <option value="todos">Todos</option>
                  {this.state.estadosDisponibles.map((estado) => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-sm-4">
              <div className="input-group input-group-sm mb-3">
                <div className="input-group-prepend">
                  <div className="input-group-text">Tipo de documento</div>
                </div>
                <select className="form-control" value={this.state.filtroDocumento} onChange={this.cambiarFiltroDocumento}>
                  <option value="todos">Todos</option>
                  {this.state.tiposDocumentoDisponibles.map((tipo) => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {this.state.cargando ? (
            <div className="d-flex justify-content-center">
              <ReactBootStrap.Spinner animation="border" />
            </div>
          ) : false}

          <div className={`row m24if-graficas ${this.state.cargando ? 'm24if-cargando' : ''}`}>

            <div className="col-md-4">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Atenciones por estado</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap">
                    <canvas ref={this.chartEstadoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Total atenciones por contrato</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap">
                    <canvas ref={this.chartContratoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Total atenciones por estado</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap m24if-canvas-wrap-lg">
                    <canvas ref={this.chartEstadoMontoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="card table-responsive">
                <div className="card-header"><h3 className="card-title">Resumen por estado</h3></div>
                <div className="card-body">
                  <table className="table table-bordered table-striped table-hover">
                    <thead>
                      <tr><th>Estado</th><th>Cantidad</th><th>Total cargos</th></tr>
                    </thead>
                    <tbody>
                      {Object.keys(resumenPorEstado).map((estado) => (
                        <tr key={estado}>
                          <td>{estado}</td>
                          <td>{resumenPorEstado[estado].cantidad}</td>
                          <td>{this.formatoMoneda(resumenPorEstado[estado].total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card table-responsive">
                <div className="card-header"><h3 className="card-title">Resumen por contrato</h3></div>
                <div className="card-body">
                  <table className="table table-bordered table-striped table-hover">
                    <thead>
                      <tr><th>Contrato</th><th>Cantidad</th><th>Total cargos</th></tr>
                    </thead>
                    <tbody>
                      {Object.keys(resumenPorContrato).map((contrato) => (
                        <tr key={contrato}>
                          <td>{contrato}</td>
                          <td>{resumenPorContrato[contrato].cantidad}</td>
                          <td>{this.formatoMoneda(resumenPorContrato[contrato].total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12 text-center">
              <span>{this.state.pagerInfo}</span>
              <p className="m24if-nota">Estos datos corresponden a la página {this.state.paginaActual} del listado de facturas. Usa la paginación para revisar otras páginas.</p>
              <nav className="nav d-flex justify-content-center">
                <ul className="pagination">
                  {this.state.pagerItems.map((item, indice) => {
                    const activo = item.activo ? 'page-item active' : 'page-item';
                    return (
                      <li key={indice} className={activo}>
                        <a className="nav-link" onClick={() => this.cargarFacturas(item.pagina || item.texto)}>{item.texto}</a>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </div>
          </div>

        </div>
      </div>
    );
  }
}

export default InformeFacturas;
